import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { CONFIG } from 'constants/config';
import { QUERY_KEYS } from 'constants/query-keys';
import { createQueryKey } from 'helpers/ReactQueryHelpers';
import { getNotices, getPersonalizedNotices } from 'services/NoticeService';

import { NOTICE_ACTIONS_TYPES, NOTICE_ACTION_MODAL_NAMES } from 'types/notice';

import type { INotices, INoticesResponse, } from 'types/notice';
import type { ReactQueryOptions } from 'types';

interface Options {
  isPersonalized?: boolean,
  page?: string,
  slot?: string
}

// Hook

export function useNotices<S = INotices>(
  options: Options = {},
  queryOptions: ReactQueryOptions<INoticesResponse, S> = {}
) {
  const { isPersonalized, page, slot } = options || {};
  const select = React.useCallback((data: INoticesResponse) => adapter(data, queryOptions?.select), [queryOptions.select]);
  return useQuery({
    queryKey: getKey(options),
    queryFn: () => (isPersonalized
      ? getPersonalizedNotices({ page, slot })
      : getNotices({ page, slot })),
    staleTime: CONFIG.DURATIONS.DEFAULT_QUERY_STALE_TIME,
    ...queryOptions,
    select
  });
}

// Key

export const getKey = (options: Options = {}) => {
  const {
    isPersonalized, page, slot
  } = options || {};

  return createQueryKey([
    isPersonalized ? QUERY_KEYS.NOTICES_PERSONALIZED : QUERY_KEYS.NOTICES,
    page,
    slot
  ]);
};

// Adapter

function adapter<S = INoticesResponse[]>(
  data: INoticesResponse,
  select?: (data: INoticesResponse) => S
): S | INotices {
  if (!select) {
    return toNotices(data);
  }
  return select(data);
}

function toNotices(data: INoticesResponse): INotices {
  return data.map((notice) => {
    const {
      id,
      title,
      body,
      icon,
      page,
      slot,
      theme,
      action
    } = notice || {};

    const { light } = theme || {};

    const {
      backgroundColor,
      titleTextColor,
      bodyTextColor,
      textColor,
      icon: themeIcon
    } = light || {};

    return {
      meta: {
        id,
        page,
        slot,
      },
      details: {
        title,
        description: body,
        icon: themeIcon || icon,
        theme: {
          backgroundColor,
          titleTextColor: titleTextColor || textColor,
          bodyTextColor: bodyTextColor || textColor,
          iconBackgroundColor: backgroundColor,
        }
      },
      action: !action ? undefined : {
        type: action.type,
        ...((action.type === NOTICE_ACTIONS_TYPES.LINK && action.url)
          ? { url: action.url }
          : {}),
        ...((action.type === NOTICE_ACTIONS_TYPES.MODAL && action.name && isEnumValue(NOTICE_ACTION_MODAL_NAMES, action.name))
          ? { name: action.name }
          : {})
      }
    };
  });
}

function isEnumValue<T extends object>(enumObj: T, value: unknown): value is T[keyof T] {
  return Object.values(enumObj).includes(value as T[keyof T]);
}
